import React, { useEffect, useState } from "react";
import { fetchVast, tagUrl, previewUrl, getCreativeOutput } from "../lib/api";
import { sanitize } from "../lib/sanitize";
import { toast } from "sonner";

function copy(text, label) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
  } else {
    const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select();
    try { document.execCommand("copy"); } catch (e) { console.error("copy failed", e); } document.body.removeChild(ta); toast.success(`${label} copied`);
  }
}

export default function ExportDialog({ open, onClose, creativeId, currentVersion, warnings, tid }) {
  const [xml, setXml] = useState("");
  const [showXml, setShowXml] = useState(false);
  const [showAdv, setShowAdv] = useState(false);
  const [out, setOut] = useState(null); // BLS-Web output { url, pinnedUrl, hashedUrl }
  const isWeb = tid === "bls-web";

  useEffect(() => {
    if (!open || !creativeId || !isWeb) return;
    setShowAdv(false); setOut(null);
    getCreativeOutput(creativeId).then(setOut).catch(() => setOut({ url: "Failed to load survey URL" }));
  }, [open, creativeId, isWeb]);

  useEffect(() => {
    if (!open || !creativeId || isWeb) return;
    setShowXml(false); setShowAdv(false); setXml("Loading…");
    fetchVast(creativeId).then(setXml).catch(() => setXml("Failed to load VAST"));
  }, [open, creativeId, isWeb]);

  if (!open) return null;
  if (isWeb) {
    return (
      <div className="scrim open" onClick={(e) => { if (e.target.classList.contains("scrim")) onClose(); }} data-testid="export-dialog">
        <div className="modal export-modal" role="dialog" aria-modal="true">
          <div className="modal-head">
            <h3>Survey URL</h3>
            <button className="iconbtn" onClick={onClose} data-testid="export-close" aria-label="Close">✕</button>
          </div>
          <div className="modal-body">
            <div className="eyebrow">Set as the ad's click-through URL in SpringServe <span className="dim">· always serves the latest version</span></div>
            <div className="hero-url">
              <code data-testid="web-output-url">{out ? out.url : "Loading…"}</code>
              <button className="btn primary" disabled={!out || !out.studyId} onClick={() => copy(out.url, "Survey URL")} data-testid="copy-web-url">Copy</button>
            </div>
            <div className="hero-sub">
              {out && out.studyId && <a className="linkbtn" href={out.url} target="_blank" rel="noopener noreferrer" data-testid="open-web-survey">Open survey ↗</a>}
            </div>
            <p className="hint-inline">Macros in braces (<code>{"{CAMPAIGN_ID}"}</code>, <code>{"{ARM}"}</code>, <code>{"{IMP_TOKEN}"}</code>…) are filled by SpringServe; the TV launcher fills device macros before opening the page full screen. The control arm is a separate launcher ad with <code>arm=control</code> — set up in SpringServe.</p>
            <button className="disclosure" onClick={() => setShowAdv((v) => !v)} data-testid="toggle-advanced">
              <span className={`chev ${showAdv ? "open" : ""}`}>›</span> Advanced — pinned and immutable URLs
            </button>
            {showAdv && out && out.studyId && (
              <div className="disclosure-body" data-testid="advanced-body">
                <div className="eyebrow">Pinned @v{out.version}</div>
                <div className="urlbox"><code>{out.pinnedUrl}</code></div>
                {out.hashedUrl && (<><div className="eyebrow" style={{ marginTop: 10 }}>Content-hashed (immutable, CDN-cacheable)</div>
                  <div className="urlbox"><code data-testid="web-hashed-url">{out.hashedUrl}</code></div></>)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  const curUrl = tagUrl(creativeId);
  const pinnedUrl = tagUrl(`${creativeId}@${currentVersion}`);
  const prev = previewUrl(creativeId);

  const download = () => {
    const blob = new Blob([xml], { type: "application/xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `cloudtv_${creativeId}_vast.xml`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 1000); toast.success("Downloaded");
  };

  return (
    <div className="scrim open" onClick={(e) => { if (e.target.classList.contains("scrim")) onClose(); }} data-testid="export-dialog">
      <div className="modal export-modal" role="dialog" aria-modal="true">
        <div className="modal-head">
          <h3>Export VAST tag</h3>
          <button className="iconbtn" onClick={onClose} data-testid="export-close" aria-label="Close">✕</button>
        </div>
        <div className="modal-body">
          {warnings && warnings.length > 0 && (
            <div className="callout warn compact" data-testid="export-warnings">
              <span>⚠</span><div dangerouslySetInnerHTML={{ __html: sanitize(warnings.join("<br>")) }} />
            </div>
          )}

          {/* Hero: the one thing most people need */}
          <div className="eyebrow">VAST tag URL <span className="dim">· always serves the latest version</span></div>
          <div className="hero-url">
            <code data-testid="tag-url-current">{curUrl}</code>
            <button className="btn primary" onClick={() => copy(curUrl, "Tag URL")} data-testid="copy-tag-current">Copy</button>
          </div>
          <div className="hero-sub">
            <a className="linkbtn" href={prev} target="_blank" rel="noopener noreferrer" data-testid="open-preview">Open preview ↗</a>
            <a className="linkbtn" href={curUrl} target="_blank" rel="noopener noreferrer">Open XML ↗</a>
          </div>
          <p className="hint-inline">Paste this into your ad server. Renders on any SIMID-capable player (IMA + equivalents); non-SIMID players simply play the video.</p>

          {/* Progressive disclosure */}
          <button className="disclosure" onClick={() => setShowAdv((v) => !v)} data-testid="toggle-advanced">
            <span className={`chev ${showAdv ? "open" : ""}`}>›</span> Advanced — pinned version URL
          </button>
          {showAdv && (
            <div className="disclosure-body" data-testid="advanced-body">
              <div className="eyebrow">Pinned @v{currentVersion} <span className="dim">· immutable, won't change if you edit</span></div>
              <div className="urlbox"><code data-testid="tag-url-pinned">{pinnedUrl}</code></div>
              <button className="btn sm" onClick={() => copy(pinnedUrl, "Pinned tag URL")} data-testid="copy-tag-pinned">Copy pinned URL</button>
            </div>
          )}

          <button className="disclosure" onClick={() => setShowXml((v) => !v)} data-testid="toggle-xml">
            <span className={`chev ${showXml ? "open" : ""}`}>›</span> View raw VAST 4.2 XML
          </button>
          {showXml && (
            <div className="disclosure-body" data-testid="xml-body">
              <pre className="code" data-testid="vast-xml">{xml}</pre>
              <div className="urlacts">
                <button className="btn sm" onClick={download} data-testid="download-xml">Download .xml</button>
                <button className="btn sm" onClick={() => copy(xml, "VAST XML")} data-testid="copy-xml">Copy XML</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
